import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import MovieModal from "@/components/base/MovieModal";
import { featuredMovies } from "@/mocks/movies";
import type { Movie } from "@/pages/home/types";

const toneMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function FeaturedMovies() {
  const [selected, setSelected] = useState<Movie | null>(null);

  return (
    <section id="featured" className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-content mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <Reveal>
            <SectionHeading
              eyebrow="本周热映"
              title="本周值得优先打开的高分影片"
              desc="以下条目综合了近期点开热度与评分表现，涵盖科幻、悬疑、剧情与动画。点开任意卡片可以查看简介、时长与可用画质。"
            />
          </Reveal>
          <Reveal delay={120}>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-refresh-line text-primary-500"></i>
              片库每日更新，条目与筛选结果以页面当前内容为准
            </div>
          </Reveal>
        </div>

        <div className="mt-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {featuredMovies.map((movie, index) => (
            <Reveal key={movie.id} delay={(index % 4) * 70}>
              <button
                type="button"
                onClick={() => setSelected(movie as Movie)}
                className="group w-full text-left cursor-pointer"
                aria-label={`查看 ${movie.title} 详情`}
              >
                <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 影片封面`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-primary-950/80 text-accent-400 text-xs font-bold backdrop-blur-sm">
                    <i className="ri-star-fill mr-1"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="absolute top-3 right-3 px-2 py-1 rounded-md bg-background-50/90 text-foreground-800 text-[11px] font-medium">
                    {movie.quality}
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/45 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-play-fill text-2xl"></i>
                    </span>
                  </span>
                </div>
                <div className="mt-3">
                  <h3 className="font-heading text-base font-bold text-foreground-950 truncate">
                    {movie.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {movie.genre} · {movie.year} · {movie.duration}
                  </p>
                  <span className={`mt-2 inline-block px-2 py-0.5 rounded-md text-[11px] ${toneMap[movie.tone]}`}>
                    {movie.tags[0]}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <MovieModal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.title ?? ""}
        poster={selected?.poster}
        quality={selected?.quality}
        blurb={selected?.blurb ?? ""}
        tags={selected?.tags ?? []}
        meta={[
          { label: "题材", value: selected?.genre ?? "-" },
          { label: "年份", value: selected ? String(selected.year) : "-" },
          { label: "地区", value: selected?.region ?? "-" },
          { label: "时长", value: selected?.duration ?? "-" },
        ]}
      />
    </section>
  );
}